import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { API_BASE_URL } from '../../core/config/api.config';
import { UpdateUserPayload, UserProfile, UserRole } from '../../core/models/user.models';
import { AdminUsersService } from '../../core/services/admin-users.service';
import { AuthService } from '../../core/services/auth.service';
import { StorageService } from '../../core/services/storage.service';

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.scss',
})
export class ProfilePageComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly usersService = inject(AdminUsersService);
  private readonly storageService = inject(StorageService);
  private readonly fb = inject(FormBuilder);

  user: UserProfile | null = null;
  loading = true;
  saving = false;
  uploadingAvatar = false;
  errorMessage = '';
  successMessage = '';

  readonly form = this.fb.group({
    firstname: ['', [Validators.required, Validators.minLength(2)]],
    lastname: ['', [Validators.required, Validators.minLength(2)]],
    displayName: [''],
    email: ['', [Validators.required, Validators.email]],
    biography: [''],
    avatarUrl: [''],
  });

  async ngOnInit(): Promise<void> {
    await this.reloadProfile();
  }

  async save(): Promise<void> {
    if (this.form.invalid || this.saving || this.uploadingAvatar) {
      this.form.markAllAsTouched();
      return;
    }

    const userId = this.user?.id;
    if (!userId) {
      this.errorMessage = 'Profil utilisateur introuvable.';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const value = this.form.getRawValue();
      const payload: UpdateUserPayload = {
        role: this.normalizeRole(this.user?.role),
        enabled: this.user?.enabled !== false,
        firstname: value.firstname?.trim() || null,
        lastname: value.lastname?.trim() || null,
        displayName: value.displayName?.trim() || null,
        biography: value.biography?.trim() || null,
        avatarUrl: value.avatarUrl?.trim() || null,
        email: value.email?.trim() || null,
      };

      await firstValueFrom(this.usersService.update(userId, payload));
      await this.reloadProfile();
      this.successMessage = 'Profil mis à jour.';
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Impossible de mettre à jour le profil.');
    } finally {
      this.saving = false;
    }
  }

  async onAvatarFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.item(0);
    if (!file || this.uploadingAvatar) {
      return;
    }

    this.uploadingAvatar = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const fileName = await firstValueFrom(this.storageService.uploadFile(file));
      this.form.patchValue({ avatarUrl: fileName });
      this.successMessage = 'Image uploadée. Pense à enregistrer ton profil.';
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Upload avatar impossible.');
    } finally {
      this.uploadingAvatar = false;
      if (input) {
        input.value = '';
      }
    }
  }

  avatarPreviewUrl(): string | null {
    const rawValue = this.form.get('avatarUrl')?.value;
    const avatar = this.normalizeOptionalString(rawValue);
    if (!avatar) {
      return null;
    }
    if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
      return avatar;
    }
    const normalizedAvatar = avatar.replace(/^\/+/, '');
    return `${API_BASE_URL}/storage/files/${normalizedAvatar}`;
  }

  private async reloadProfile(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    try {
      const profile = await firstValueFrom(this.auth.refreshProfile());
      if (!profile) {
        this.user = null;
        this.errorMessage = 'Profil introuvable.';
        return;
      }
      this.user = profile;
      this.form.reset({
        firstname: profile.member?.firstname ?? '',
        lastname: profile.member?.lastname ?? '',
        displayName: profile.member?.displayName ?? '',
        email: profile.email ?? profile.member?.email ?? '',
        biography: profile.member?.biography ?? '',
        avatarUrl: profile.member?.avatarUrl ?? '',
      });
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Impossible de charger le profil.');
    } finally {
      this.loading = false;
    }
  }

  private normalizeOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeRole(role: string | UserRole | undefined): UserRole | undefined {
    if (role === 'USER' || role === 'MANAGER' || role === 'ADMIN' || role === 'ADMIN_READ_ONLY') {
      return role;
    }
    return undefined;
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    if (error && typeof error === 'object') {
      const record = error as { error?: { message?: unknown }; message?: unknown };
      if (typeof record.error?.message === 'string' && record.error.message.trim().length > 0) {
        return record.error.message;
      }
      if (typeof record.message === 'string' && record.message.trim().length > 0) {
        return record.message;
      }
    }

    return fallback;
  }
}
